/* eslint-disable jsx-a11y/no-autofocus */

import React from 'react';
import pt from 'prop-types';

import { singularize } from 'utils';

import Link from 'components/link';
import Field from 'components/form/field';
import Tooltip from 'components/tooltip';
import ButtonLink from 'components/button/button-link';
import Icon from 'components/icon';
import { DropdownMenu, DropdownMenuItem } from 'components/dropdown-menu';
import StorefrontEditorIcon from 'components/storefront/editor/icon';
import { classNames } from 'utils';

import TemplateDropdown from './template-dropdown';

import './sub-header.scss';

const ICON_SIZE = 18;

const contentRest = Object.freeze({
  side: 'bottom',
  sideOffset: 10,
  align: 'end',
});

export default class StorefrontEditorSubHeader extends React.PureComponent {
  static propTypes = {
    lookup: pt.object,
    page: pt.object,
    pageQuery: pt.object,
    pageRecord: pt.object,
    altTemplate: pt.string,
    primaryThemeTemplates: pt.object,
    onCreateAlternateTemplate: pt.func,
    onRenameAlternateTemplate: pt.func,
    onDeleteAlternateTemplate: pt.func,
    onChangeAlternateTemplate: pt.func,
    onChangePageRecord: pt.func,
  };

  onChangeRecordItem = (_event, record) => {
    const { pageRecord, onChangePageRecord } = this.props;
    const shouldChangeRecord = record && record.id !== pageRecord.id;

    if (shouldChangeRecord) {
      onChangePageRecord(record);
    }
  };

  getRecordLabel(record) {
    const label =
      record.name || record.title || record.number || record.slug || record.id;

    return (
      <span className="storefront-editor--record-dropdown--item-label">
        {label}
      </span>
    );
  }

  getCollectionName(plural = false) {
    const { page } = this.props;
    const parts = page.collection.split('/');
    const name = parts[parts.length - 1];

    return plural ? name : singularize(name);
  }

  renderLookupItems = ({ lookup, selected, onClick, onMouseOver }) => {
    const { page, pageRecord } = this.props;
    const { collection } = page;
    const collectionName = this.getCollectionName();

    const lookupItems = lookup.results.map((record) => {
      const itemSelected = selected === record;
      return (
        <DropdownMenuItem
          key={record.id}
          data-id={record.id}
          role="option"
          aria-selected={itemSelected}
          className={classNames('storefront-editor--record-dropdown--item', {
            selected: itemSelected,
          })}
          onClick={onClick}
          onMouseOver={onMouseOver}
        >
          {this.getRecordLabel(record)}
          {record.id === pageRecord.id && (
            <StorefrontEditorIcon
              className="storefront-editor--record-dropdown--icon-check"
              type="check"
              width={ICON_SIZE}
              height={ICON_SIZE}
            />
          )}
        </DropdownMenuItem>
      );
    });

    const newItem = (
      <Link
        key="new"
        className="storefront-editor--record-dropdown--new-item"
        to={`/${collection}/new`}
        target="blank"
      >
        New {collectionName}
        <Icon fa="arrow-up-right-from-square" />
      </Link>
    );

    lookupItems.push(newItem);

    return lookupItems;
  };

  renderRecordDropdownContent() {
    const { lookup, page, pageQuery, pageRecord } = this.props;
    const { collection } = page;

    const placeholder = `Find ${this.getCollectionName()}`;

    return (
      <Field
        type="lookup"
        model={collection}
        lookup={lookup}
        aria-label={placeholder}
        placeholder={placeholder}
        renderLookupItems={this.renderLookupItems}
        onChange={this.onChangeRecordItem}
        defaultValue={pageRecord}
        query={pageQuery}
        queryFocus
        autoFocus
        mountQuery
        select
      />
    );
  }

  renderRecordDropdownTrigger() {
    const { pageRecord } = this.props;

    return (
      <>
        {this.getRecordLabel(pageRecord)}
        <Icon fa="chevron-down" width={ICON_SIZE} height={ICON_SIZE} />
      </>
    );
  }

  renderTooltipMismatch() {
    const { page, primaryThemeTemplates } = this.props;

    if (!primaryThemeTemplates[page.collection]) {
      return null;
    }

    const recordTemplate = this.props.pageRecord?.theme_template || null;
    const selectedTemplate = this.props.altTemplate || null;

    if (recordTemplate === selectedTemplate) {
      return null;
    }

    return (
      <Tooltip
        arrow
        side="bottom"
        align="center"
        message="Template mismatch. Change it in the ’Page’ tab."
        maxTextLineWidth={25}
        contentClassName="popover-content storefront-editor--record-dropdown--tooltip-content"
      >
        <Icon fa="circle-info" />
      </Tooltip>
    );
  }

  renderRecordDropdown() {
    return (
      <DropdownMenu
        contentClassName="dropdown-menu-content storefront-editor--record-dropdown--content"
        triggerClassName="button button-sm button-secondary storefront-editor--record-dropdown"
        triggerChildren={this.renderRecordDropdownTrigger()}
        contentRest={contentRest}
      >
        {this.renderRecordDropdownContent()}
      </DropdownMenu>
    );
  }

  renderOpenRecordButton() {
    const { page, pageRecord } = this.props;

    return (
      <ButtonLink
        size="sm"
        type="secondary"
        to={`/${page.collection}/${pageRecord.id}`}
        className="button-icon storefront-editor--sub-header--open-record"
        target="blank"
      >
        <Icon fa="arrow-up-right-from-square" />
      </ButtonLink>
    );
  }

  renderNoRecordContent() {
    const { page } = this.props;
    const { collection } = page;
    const pluralCollectionName = this.getCollectionName(true);

    return (
      <div className="storefront-editor--sub-header--no-record">
        <span className="storefront-editor--sub-header--no-record-text">
          You don’t have any {pluralCollectionName} yet.
        </span>
        <ButtonLink
          className="storefront-editor--sub-header--no-record-button"
          to={`/${collection}/new`}
          target="blank"
          type="secondary"
        >
          Create {singularize(pluralCollectionName)}
        </ButtonLink>
      </div>
    );
  }

  renderAltTemplateDropdown() {
    const {
      page,
      pageRecord,
      altTemplate,
      primaryThemeTemplates,
      onCreateAlternateTemplate,
      onRenameAlternateTemplate,
      onDeleteAlternateTemplate,
      onChangeAlternateTemplate,
    } = this.props;

    if (!primaryThemeTemplates[page.collection]) {
      return null;
    }

    return (
      <TemplateDropdown
        page={page}
        pageRecord={pageRecord}
        altTemplate={altTemplate}
        primaryThemeTemplates={primaryThemeTemplates}
        onCreateAlternateTemplate={onCreateAlternateTemplate}
        onRenameAlternateTemplate={onRenameAlternateTemplate}
        onDeleteAlternateTemplate={onDeleteAlternateTemplate}
        onChangeAlternateTemplate={onChangeAlternateTemplate}
      />
    );
  }

  renderContent() {
    const { pageRecord } = this.props;

    if (!pageRecord) {
      return this.renderNoRecordContent();
    }

    return (
      <>
        {this.renderAltTemplateDropdown()}

        <div className="storefront-editor--record-dropdown--wrapper">
          {this.renderTooltipMismatch()}
          {this.renderRecordDropdown()}
          {this.renderOpenRecordButton()}
        </div>
      </>
    );
  }

  render() {
    return (
      <div className="storefront-editor--sub-header">
        {this.renderContent()}
      </div>
    );
  }
}
